<template>
  <div>
    <menu-top/>

    <v-main>
      <div id="home" class="gr">

        <!-- ============ HERO ============ -->
        <section class="gr-hero">
          <v-container>
            <v-row align="center" justify="center" class="gr-hero-layout">
              <v-col cols="12" md="7" lg="7" class="text-center text-md-left">
                <h1 class="gr-h1 gr-hero-title">Especialistes en tràmits de vehicles i transport</h1>

                <p class="gr-lead gr-hero-lead">
                  Quatre generacions. {{ yearsOfExperience }} anys. Un sol objectiu:
                  que no t'hagis de preocupar per la paperassa.
                </p>

                <v-btn
                  :href="wa('Hola, necessito informació sobre...')"
                  target="_blank"
                  rel="noopener"
                  x-large
                  class="gr-cta gr-hero-action mt-6"
                  depressed
                >
                  <v-icon left>mdi-whatsapp</v-icon>
                  Consulta sense compromís
                </v-btn>
              </v-col>

              <v-col cols="12" md="5" lg="4" class="mt-10 mt-md-0">
                <aside class="gr-hero-card" aria-label="Serveis especialitzats">
                  <p class="gr-hero-card-label">Serveis especialitzats</p>
                  <div class="gr-hero-services">
                    <div class="gr-hero-service">
                      <v-icon>mdi-car-outline</v-icon>
                      <span>Vehicles i DGT</span>
                    </div>
                    <div class="gr-hero-service">
                      <v-icon>mdi-truck-outline</v-icon>
                      <span>Transport professional</span>
                    </div>
                    <div class="gr-hero-service">
                      <v-icon>mdi-shield-check-outline</v-icon>
                      <span>Assegurances</span>
                    </div>
                  </div>
                </aside>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ INTRO ============ -->
        <section class="gr-section gr-section--intro">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8">
                <h2 class="gr-h2">El que altres compliquen, nosaltres ho resolem</h2>

                <p class="gr-body">
                  L'any 1935 vam obrir les portes d'aquesta gestoria amb un objectiu clar:
                  ajudar les persones amb els seus tràmits.
                  I això és el que hem fet des de llavors.
                </p>

                <p class="gr-body">
                  Quatre generacions després, continuem aquí. A Tarragona. Amb més experiència i amb una manera de treballar que s'adapta
                  als temps: si vols venir a veure'ns, perfecte. Si prefereixes enviar-nos
                  els documents per WhatsApp des de casa, també.
                </p>

                <p class="gr-body gr-body--strong">
                  No som una gestoria que ho fa tot per a tothom. Som especialistes
                  en vehicles, DGT i transport professional. I aquesta especialització es nota.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ SERVEIS (ACORDIONS) ============ -->
        <section class="gr-section gr-section--alt" id="serveis">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="9">
                <h2 class="gr-h2 text-center mb-8">Què gestionem?</h2>

                <v-expansion-panels flat multiple class="gr-panels">

                  <!-- Vehicles i DGT -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-car-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Tràmits de vehicles i DGT</span>
                          <span class="gr-panel-sub">Els més habituals i els més complexos</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <ul class="gr-list">
                        <li v-for="(s, i) in vehicles" :key="'v' + i">
                          <strong>{{ s.t }}</strong><template v-if="s.d">: {{ s.d }}</template>
                        </li>
                      </ul>

                      <p class="gr-list-title">I els que altres no volen tocar:</p>
                      <ul class="gr-list gr-list--tight">
                        <li v-for="(s, i) in vehiclesDificils" :key="'vd' + i">{{ s }}</li>
                      </ul>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- Transport -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-truck-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Tràmits per a professionals del transport</span>
                          <span class="gr-panel-sub">Empreses de transport i autònoms del sector</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <ul class="gr-list">
                        <li v-for="(s, i) in transport" :key="'t' + i">
                          <strong>{{ s.t }}</strong><template v-if="s.d">: {{ s.d }}</template>
                        </li>
                      </ul>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- Assegurances -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-shield-check-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Assegurances</span>
                          <span class="gr-panel-sub">La millor assegurança, mentre gestionem el teu tràmit</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <p class="gr-body">
                        Mentre gestionem el teu tràmit, podem buscar-te la millor assegurança.
                        Sense que hagis de trucar enlloc més. Treballem amb les principals
                        companyies del mercat i oferim:
                      </p>
                      <ul class="gr-list gr-list--tight">
                        <li v-for="(s, i) in assegurances" :key="'s' + i">{{ s }}</li>
                      </ul>

                      <v-btn
                        :href="wa('Hola, vull informació sobre una assegurança de...', whatsappAssegurances)"
                        target="_blank"
                        rel="noopener"
                        depressed
                        class="gr-wa-btn mt-2"
                      >
                        <v-icon left>mdi-whatsapp</v-icon>
                        Demana'ns pressupost per WhatsApp
                      </v-btn>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Formulari de pressupost d'assegurances -->
            <v-row justify="center" class="mt-10">
              <v-col cols="12" md="9" lg="7">
                <formulario-seguro
                  lang="ca"
                  :whatsapp="whatsappAssegurances"
                  origen="home-cat"
                  titulo-tag="h3"
                  sin-margen
                />
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ EXPERIÈNCIA ============ -->
        <section class="gr-anniversary">
          <v-container>
            <v-row align="center" justify="center">
              <v-col cols="12" md="4" class="text-center">
                <p class="gr-big-number">{{ yearsOfExperience }}</p>
                <p class="gr-big-label">anys</p>
              </v-col>
              <v-col cols="12" md="7">
                <p class="gr-anniversary-text">
                  Quatre generacions resolent el que altres compliquen.
                  Les mateixes persones, el mateix carrer, la mateixa manera de treballar:
                  tu ens expliques què necessites i nosaltres ens ocupem de la resta.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ RESSENYES ============ -->
        <section class="gr-section gr-section--alt">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="8" class="text-center">
                <h2 class="gr-h2">El que diuen els qui ja han passat per aquí</h2>
                <p class="gr-stars">
                  <v-icon v-for="n in 5" :key="n" class="gr-star">mdi-star</v-icon>
                  <span class="gr-stars-text">4,9 a Google</span>
                </p>
                <p class="gr-body-sm mb-6">No és un número que hàgim triat nosaltres.</p>

                <v-carousel
                  cycle
                  :interval="7000"
                  :height="$vuetify.breakpoint.smAndDown ? 560 : 400"
                  hide-delimiter-background
                  :show-arrows="$vuetify.breakpoint.mdAndUp"
                  class="gr-carousel"
                >
                  <v-carousel-item v-for="(r, i) in ressenyes" :key="'r' + i">
                    <blockquote class="gr-quote">
                      <p class="gr-quote-text">“{{ r.text }}”</p>
                      <p class="gr-quote-author">{{ r.autor }}</p>
                    </blockquote>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ COM TREBALLEM ============ -->
        <section class="gr-section">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="9">
                <h2 class="gr-h2 text-center">Com treballem?</h2>
                <p class="gr-body-sm text-center mb-8">
                  Sense cues. Sense desplaçaments innecessaris. Sense que hagis de tornar tres vegades.
                </p>

                <ol class="gr-steps">
                  <li v-for="(p, i) in passos" :key="'p' + i" class="gr-step">
                    <span class="gr-step-num">{{ i + 1 }}</span>
                    <span class="gr-step-body">
                      <span class="gr-step-title">{{ p.t }}</span>
                      <span class="gr-step-text">{{ p.d }}</span>
                    </span>
                  </li>
                </ol>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ ON ESTEM ============ -->
        <section id="on-estem" class="gr-section gr-location">
          <v-container>
            <v-row align="center" justify="center">
              <v-col cols="12" md="5" lg="4">
                <p class="gr-eyebrow">On ens trobaràs</p>
                <h2 class="gr-h2">Al centre de Tarragona</h2>
                <p class="gr-body">
                  Som a la Rambla Nova, al costat de la Font del Centenari.
                  Vine a veure'ns o envia'ns la documentació i t'ajudem com et vagi millor.
                </p>
                <a
                  class="gr-location-link"
                  href="https://goo.gl/maps/oB7HnhuBQtyNxuJt9"
                  target="_blank"
                  rel="noopener"
                >
                  <v-icon>mdi-directions</v-icon>
                  Com arribar-hi
                </a>
              </v-col>

              <v-col cols="12" md="6" lg="5" class="mt-8 mt-md-0">
                <div class="gr-map-card">
                  <iframe
                    title="Mapa de Gestoria Riola a Tarragona"
                    src="https://www.google.com/maps?q=Rambla+Nova+110,+2%C2%BA+2%C2%AA,+43001+Tarragona&z=16&output=embed"
                    loading="lazy"
                    allowfullscreen
                    referrerpolicy="no-referrer-when-downgrade"
                  />
                  <div class="gr-map-address">
                    <v-icon>mdi-map-marker</v-icon>
                    <span>Rambla Nova, 110 · 2n 2a · 43001 Tarragona</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ FAQ ============ -->
        <section class="gr-section gr-section--faq">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8">
                <h2 class="gr-h2 text-center mb-8">Preguntes freqüents</h2>

                <v-expansion-panels flat class="gr-panels gr-panels--faq">
                  <v-expansion-panel v-for="(f, i) in faq" :key="'f' + i">
                    <v-expansion-panel-header>
                      <span class="gr-faq-q">{{ f.q }}</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <p class="gr-body">{{ f.a }}</p>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ TANCAMENT ============ -->
        <section class="gr-closing">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8" class="text-center">
                <h2 class="gr-h2 gr-h2--light">{{ yearsOfExperience }} anys resolent el que altres compliquen</h2>

                <p class="gr-closing-text">
                  Si tens un tràmit de vehicle, necessites gestions amb la DGT
                  o ets professional del transport, som aquí.
                </p>
                <p class="gr-closing-text">
                  Sense cues. Sense complicacions. Amb el suport de quatre generacions.
                </p>

                <v-btn
                  :href="wa('Hola, necessito informació sobre...')"
                  target="_blank"
                  rel="noopener"
                  x-large
                  depressed
                  class="gr-wa-btn gr-wa-btn--xl mt-6"
                >
                  <v-icon left>mdi-whatsapp</v-icon>
                  Escriu-nos per WhatsApp
                </v-btn>

                <p class="gr-closing-sign">
                  Gestoria Riola · Tarragona · Des de 1935 fent fàcil el que és difícil.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <Footer/>
      </div>
    </v-main>

    <menu-bottom/>

    <!-- ============ FAB WHATSAPP ============ -->
    <a
      :href="wa('Hola, necessito informació sobre...')"
      target="_blank"
      rel="noopener"
      class="gr-fab"
      aria-label="Escriu-nos per WhatsApp"
    >
      <v-icon class="gr-fab-icon">mdi-whatsapp</v-icon>
      <span class="gr-fab-text">Escriu-nos</span>
    </a>

    <!-- ============ BOTÓ TORNAR A DALT ============ -->
    <button
      v-show="showTop"
      type="button"
      class="gr-totop"
      aria-label="Tornar a dalt"
      @click="scrollToTop"
    >
      <v-icon class="gr-totop-icon">mdi-chevron-up</v-icon>
    </button>
  </div>
</template>

<script>
  import MenuTop from "@/components/MenuTopCat";
  import MenuBottom from "@/components/MenuBottomCat";
  import Footer from "@/components/FooterCat";
  import { mapGetters } from 'vuex';
  import FormularioSeguro from '@/components/FormularioSeguro';

  export default {
    name: 'PageHome',
    data: () => ({
      whatsapp: '34639086934',
      whatsappAssegurances: '34624217274',

      showTop: false,

      vehicles: [
        {
          t: 'Canvi de nom de vehicles',
          d: "el tràmit més sol·licitat. Hi ha molts tipus i molts factors que afecten el preu i la documentació. Envia'ns la informació i et diem exactament què necessites i quant costa, sense compromís."
        },
        {
          t: 'Notificació de venda',
          d: 'si véns un cotxe, això et protegeix. Tràmit ràpid, econòmic i amb preu fix.'
        },
        {
          t: 'Matriculació de vehicles nous i importats',
          d: 'amb tota la documentació en ordre, en menys de 24 hores tens número de matrícula.'
        },
        {
          t: 'Informes de vehicles',
          d: 'sabem si el teu vehicle té càrregues, embargaments, reserves de domini o impostos pendents. Només amb la matrícula.'
        },
        { t: 'Duplicats de permisos de circulació i carnets de conduir', d: '' },
        { t: 'Baixes temporals i definitives', d: '' },
        { t: 'Gestió d\'impostos vinculats al vehicle', d: 'IVTM, ITP, IEDMT, CO2.' },
        { t: 'Inscripció i cancel·lació al Registre de Béns Mobles', d: '' }
      ],

      vehiclesDificils: [
        'Transferències per herència',
        'Matriculacions de vehicles agrícoles amb canvis al ROMA',
        'Vehicles que requereixen certificats de transport'
      ],

      transport: [
        {
          t: 'Targetes de tacògraf',
          d: 'renovacions, targetes noves, duplicats i canvis. En uns 15 dies des que ho sol·licitem.'
        },
        {
          t: 'Autoritzacions i targetes de transport',
          d: 'MPC, MDL, MDP, OT, VT, VTC, VD. Qualsevol tipus.'
        },
        { t: 'Certificats de conductor extracomunitari', d: '' },
        {
          t: 'Llicències comunitàries per a transport internacional',
          d: 'UE, Islàndia, Noruega, Liechtenstein i Suïssa. També autoritzacions bilaterals amb el Marroc i Andorra.'
        },
        {
          t: 'Competència professional de transportista',
          d: 'títol de gestor, convalidacions i canvi de gestor.'
        },
        {
          t: 'Permisos especials de transport',
          d: 'vehicles que superen el pes o les dimensions màximes.'
        },
        { t: 'Gestió del cens del gasoil professional', d: '' },
        { t: 'Assessorament davant inspeccions i ajuts per a transportistes', d: '' }
      ],

      assegurances: [
        'Vehicles: cotxes, motos, taxis, furgonetes, camions i flotes',
        'Llar i comunitats',
        'Comerços, professionals i PIMES',
        'Transport de mercaderies',
        'Salut, vida i accidents',
        'Viatges i responsabilitat civil'
      ],

      ressenyes: [
        {
          text: "Necessitava fer un canvi de titularitat d'un cotxe de segona mà que acabava de comprar, i no podia ser més senzill i econòmic. A través de WhatsApp vaig enviar tota la documentació necessària i l'endemà ja tenia el permís de circulació. Moltíssimes gràcies, Gestoria Riola!",
          autor: 'Lukas'
        },
        {
          text: "Aquesta és la meva segona vegada amb ells. Són súper eficients. El preu boníssim. No vaig haver de fer cua. En amb prou feines 7 minuts estava fet el canvi de nom. Són de les poques empreses que he vist amb 4,9 estrelles a Google… ben merescudes.",
          autor: 'Pedro'
        },
        {
          text: "Molt agraït a l'Alex i a tot el seu equip. Em van ajudar a gestionar la compra i l'exportació del meu cotxe a Lituània de manera ràpida i professional. Comunicació clara, tracte amable i total serietat. Sens dubte els recomano.",
          autor: 'Paulius'
        },
        {
          text: "Sens cap mena de dubte la millor gestoria de Tarragona. Serietat, professionalitat, però sobretot bona gent l'Alex i tot el seu equip. T'orienten en tots els teus dubtes amb paciència i amabilitat (qualitats difícils de trobar avui dia). Des que vaig arribar a Espanya totes les meves compres i vendes de cotxes han passat per aquesta gestoria i no tinc més que paraules d'agraïment pel tracte rebut. Quan un servei és de qualitat mereix ser reconegut, i no em canso de recomanar-los cada vegada que algú em pregunta per una gestoria de confiança.",
          autor: 'Micaela'
        }
      ],

      passos: [
        { t: "Explica'ns què necessites", d: 'Per WhatsApp, per telèfon o en persona. Com prefereixis.' },
        { t: 'Et diem exactament quins documents calen', d: 'I el cost total, sense sorpreses.' },
        { t: "Ens envies la documentació", d: 'Per WhatsApp, per email o en persona. Com et vagi més bé.' },
        { t: 'Nosaltres ho gestionem tot', d: 'Tu no has de fer res més.' },
        { t: 'T\'avisem quan està llest', d: 'Normalment en menys de 24 hores. De vegades el mateix dia. I si necessites que t\'ho enviem a casa, també ho fem.' }
      ],

      faq: [
        {
          q: 'Quant costa el canvi de nom?',
          a: "Depèn del vehicle i del tipus d'operació. Envia'ns la documentació i et diem el preu exacte sense compromís."
        },
        {
          q: 'Quant triga?',
          a: 'Si no hi ha cap impediment amb el vehicle, en menys de 24 hores tens el permís de circulació.'
        },
        {
          q: 'El vehicle té càrregues o embargaments?',
          a: 'Només amb la matrícula t\'ho diem a l\'instant, sense cost.'
        },
        {
          q: "Puc fer-ho tot sense venir a l'oficina?",
          a: 'Sí. Molts clients ho gestionen tot per WhatsApp de principi a fi.'
        },
        {
          q: 'Feu assegurances?',
          a: 'Sí. Mentre gestionem el teu tràmit, podem buscar-te la millor assegurança del mercat.'
        },
        {
          q: 'Envieu la documentació a domicili?',
          a: 'Sí. Tots els enviaments són certificats i surten el mateix dia que rebem la documentació. L\'endemà és a casa teva.'
        }
      ]
    }),

    computed: {
      ...mapGetters(['collapseSidebar']),
      yearsOfExperience() {
        return new Date().getFullYear() - 1935;
      },
    },

    mounted() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    },

    beforeDestroy() {
      window.removeEventListener('scroll', this.onScroll);
    },

    methods: {
      onScroll() {
        this.showTop = window.pageYOffset > 400;
      },

      scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },

      wa(missatge, numero) {
        const desti = numero || this.whatsapp;
        return 'https://wa.me/' + desti + '?text=' + encodeURIComponent(missatge);
      }
    },

    components: {
      FormularioSeguro,
      Footer,
      MenuTop,
      MenuBottom
    }
  }
</script>

<style scoped>
  .gr {
    --gr-slate: #454E4F;
    --gr-brand: #900A30;
    --gr-brand-dark: #6E0724;
    --gr-gold: #F2A900;
    --gr-ink: #252A2B;
    --gr-mute: rgba(37, 42, 43, 0.72);
    --gr-alt: #F4F7F9;
  }

  /* ---------- Tipografia base ---------- */
  .gr-eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--gr-brand);
    margin-bottom: 12px;
  }

  .gr-h1 {
    font-size: 2.6rem;
    line-height: 1.12;
    font-weight: 700;
    color: var(--gr-slate);
    letter-spacing: -0.02em;
  }

  .gr-h2 {
    font-size: 1.85rem;
    line-height: 1.2;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 20px;
    letter-spacing: -0.01em;
  }

  .gr-h2--light { color: #fff; }

  .gr-h3 {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 4px;
  }

  .gr-lead {
    font-size: 1.2rem;
    line-height: 1.55;
    color: var(--gr-mute);
    margin-top: 18px;
  }

  .gr-body {
    font-size: 1.05rem;
    line-height: 1.7;
    color: var(--gr-mute);
    margin-bottom: 18px;
  }

  .gr-body--strong {
    color: var(--gr-ink);
    font-weight: 600;
  }

  .gr-body-sm {
    font-size: 0.98rem;
    line-height: 1.6;
    color: var(--gr-mute);
  }

  .gr-caption {
    font-size: 0.85rem;
    color: var(--gr-mute);
  }

  /* ---------- Seccions ---------- */
  #home .gr-hero {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: 72px 0 56px;
    background-image:
      linear-gradient(135deg, rgba(37, 42, 43, 0.86) 0%, rgba(37, 42, 43, 0.62) 52%, rgba(144, 10, 48, 0.52) 100%),
      url("~@/assets/img/camiones.jpeg");
    background-size: cover, cover;
    background-position: center, center;
    background-repeat: no-repeat, no-repeat;
  }

  /* Text del hero en clar sobre la foto */
  #home .gr-hero .gr-hero-eyebrow { color: var(--gr-gold); }
  #home .gr-hero .gr-hero-title { color: #fff; }
  #home .gr-hero .gr-hero-lead { color: rgba(255, 255, 255, 0.92); }
  #home .gr-hero .gr-hero-support { color: rgba(255, 255, 255, 0.9); }
  #home .gr-hero .gr-hero-support .v-icon { color: var(--gr-gold); }

  /* Mòbil: la foto omple tot el hero sense bandes ni talls */
  @media (max-width: 600px) {
    #home .gr-hero {
      background-color: var(--gr-ink);
      background-size: cover, cover;
      background-position: center, center 65%;
    }
  }

  .gr-hero::before,
  .gr-hero::after {
    content: '';
    position: absolute;
    z-index: -1;
    border: 1px solid rgba(144, 10, 48, 0.10);
    border-radius: 50%;
    pointer-events: none;
  }

  .gr-hero::before {
    width: 460px;
    height: 460px;
    top: -315px;
    right: 8%;
  }

  .gr-hero::after {
    width: 260px;
    height: 260px;
    bottom: -190px;
    left: 10%;
  }

  .gr-hero .v-container {
    position: relative;
    z-index: 1;
  }

  .gr-hero-eyebrow,
  .gr-hero-title,
  .gr-hero-lead,
  .gr-hero-action,
  .gr-hero-card {
    animation: gr-hero-reveal 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-hero-eyebrow { animation-delay: 110ms; }
  .gr-hero-title { animation-delay: 190ms; }
  .gr-hero-lead { animation-delay: 290ms; }
  .gr-hero-action { animation-delay: 390ms; }
  .gr-hero-card { animation-delay: 250ms; }

  .gr-hero-title {
    font-size: 3.15rem;
    line-height: 1.08;
    max-width: 700px;
  }

  .gr-hero-lead {
    max-width: 610px;
  }

  .gr-hero-support {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 22px 0 0;
    color: var(--gr-slate);
    font-size: 0.92rem;
    font-weight: 500;
  }

  .gr-hero-support .v-icon {
    color: var(--gr-brand);
  }

  .gr-hero-card {
    position: relative;
    overflow: hidden;
    padding: 32px 30px 28px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(69, 78, 79, 0.13);
    border-radius: 20px;
    box-shadow: 0 22px 52px rgba(37, 42, 43, 0.12);
  }

  .gr-hero-card-label {
    margin: 0 0 14px;
    color: var(--gr-brand);
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-align: center;
    text-transform: uppercase;
  }

  .gr-hero-services {
    border-top: 1px solid rgba(69, 78, 79, 0.13);
  }

  .gr-hero-service {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid rgba(69, 78, 79, 0.13);
    color: var(--gr-slate);
    font-size: 0.98rem;
    font-weight: 600;
  }

  .gr-hero-service .v-icon {
    color: var(--gr-brand);
    font-size: 22px;
  }

  .gr-hero-action.v-btn {
    box-shadow: 0 10px 24px rgba(144, 10, 48, 0.22);
    transition: transform 180ms ease, box-shadow 180ms ease;
  }

  .gr-hero-action.v-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 30px rgba(144, 10, 48, 0.28);
  }

  @keyframes gr-hero-reveal {
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .gr-section { padding: 64px 0; }

  .gr-section--alt { background-color: var(--gr-alt); }

  #home .gr-section--intro,
  #home .gr-section--faq {
    position: relative;
  }

  #home .gr-section--intro {
    background: linear-gradient(135deg, #efe6e9 0%, #f7f1f3 70%);
    box-shadow: inset 0 14px 26px -18px rgba(37, 42, 43, 0.22);
  }

  #home .gr-section--faq { background: #fff; }

  .gr-section--intro::before,
  .gr-section--faq::before {
    content: '';
    position: absolute;
    top: -42px;
    left: 0;
    width: 100%;
    height: 43px;
    pointer-events: none;
    clip-path: polygon(0 100%, 100% 0, 100% 100%);
  }

  .gr-section--intro::before { background: #efe6e9; }
  .gr-section--faq::before { background: #fff; }

  /* Acento de marca que marca el inicio de la sección */
  #home .gr-section--intro .gr-h2 { position: relative; padding-top: 22px; }

  #home .gr-section--intro .gr-h2::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 54px;
    height: 4px;
    border-radius: 999px;
    background: var(--gr-brand);
  }

  /* ---------- CTA ---------- */
  .gr-cta.v-btn {
    background-color: var(--gr-brand) !important;
    color: #fff !important;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    border-radius: 999px;
    padding: 0 30px !important;
  }

  .gr-cta--form.v-btn {
    border-radius: 8px;
    margin-top: 8px;
  }

  /* ---------- Acordions ---------- */
  .gr-panels >>> .v-expansion-panel {
    background-color: #fff;
    border: 1px solid rgba(69, 78, 79, 0.16);
    border-radius: 10px !important;
    margin-bottom: 12px;
  }

  .gr-panels >>> .v-expansion-panel::before { box-shadow: none; }

  .gr-panel-head {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .gr-panel-icon.v-icon {
    color: var(--gr-brand);
    font-size: 30px;
  }

  .gr-panel-title {
    display: block;
    font-size: 1.12rem;
    font-weight: 700;
    color: var(--gr-slate);
    line-height: 1.3;
  }

  .gr-panel-sub {
    display: block;
    font-size: 0.9rem;
    color: var(--gr-mute);
    margin-top: 2px;
  }

  .gr-list {
    padding-left: 20px;
    margin: 0 0 8px;
  }

  .gr-list li {
    font-size: 1rem;
    line-height: 1.65;
    color: var(--gr-mute);
    margin-bottom: 10px;
  }

  .gr-list li strong { color: var(--gr-ink); }

  .gr-list--tight li { margin-bottom: 6px; }

  .gr-list-title {
    font-weight: 700;
    color: var(--gr-slate);
    margin: 18px 0 8px;
  }

  .gr-faq-q {
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--gr-slate);
  }

  /* ---------- Formulari ---------- */
  .gr-form-card {
    background: #fff;
    border: 1px solid rgba(69, 78, 79, 0.16);
    border-radius: 14px;
    padding: 32px;
  }

  .gr-wa-btn--xl.v-btn {
    height: 52px;
    font-size: 1.05rem;
    padding: 0 32px !important;
  }

  .gr-wa-btn.v-btn {
    background-color: #25D366 !important;
    color: #fff !important;
    font-weight: 700;
    text-transform: none;
    border-radius: 999px;
    height: auto !important;
    min-height: 48px;
    max-width: 100%;
    white-space: normal;
    padding: 10px 22px !important;
  }

  /* Permite que el texto largo del botón se parta en móvil sin desbordar */
  .gr-wa-btn.v-btn .v-btn__content {
    white-space: normal;
    max-width: 100%;
    flex: 1 1 auto;
    line-height: 1.25;
  }

  .gr-wa-alt {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid rgba(69, 78, 79, 0.16);
  }

  .gr-wa-alt-text {
    font-size: 0.95rem;
    color: var(--gr-mute);
  }

  /* ---------- Experiència ---------- */
  #home .gr-anniversary {
    min-height: 360px;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    background:
      radial-gradient(circle at 13% 50%, rgba(255, 255, 255, 0.10) 0 1px, transparent 1.5px),
      radial-gradient(circle at 13% 50%, rgba(255, 255, 255, 0.04) 0 110px, transparent 260px),
      linear-gradient(112deg, #6E0724 0%, #790827 52%, #900A30 100%);
    color: #fff;
    padding: 42px 0;
  }

  .gr-anniversary::before,
  .gr-anniversary::after {
    content: '';
    position: absolute;
    z-index: -1;
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 50%;
    pointer-events: none;
  }

  .gr-anniversary::before {
    width: 430px;
    height: 430px;
    top: -310px;
    right: 7%;
  }

  .gr-anniversary::after {
    width: 270px;
    height: 270px;
    bottom: -190px;
    left: 32%;
  }

  .gr-anniversary .v-container {
    position: relative;
    z-index: 1;
  }

  .gr-big-number {
    font-size: 8rem;
    line-height: 0.9;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.05em;
    margin: 0;
    animation: gr-anniversary-reveal 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
    transition: transform 220ms ease;
  }

  .gr-big-label {
    text-transform: uppercase;
    letter-spacing: 0.3em;
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
    margin: 8px 0 0;
    animation: gr-anniversary-reveal 650ms 100ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-anniversary-text {
    font-size: 1.25rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    animation: gr-anniversary-reveal 700ms 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-anniversary:hover .gr-big-number {
    transform: translateY(-4px);
  }

  @keyframes gr-anniversary-reveal {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ---------- Equip ---------- */
  .gr-team-photo {
    border-radius: 14px;
    overflow: hidden;
  }

  /* ---------- Ressenyes ---------- */
  .gr-stars { margin-bottom: 4px; }

  .gr-star.v-icon {
    color: var(--gr-gold);
    font-size: 22px;
  }

  .gr-stars-text {
    font-weight: 700;
    color: var(--gr-slate);
    margin-left: 10px;
    vertical-align: middle;
  }

  .gr-carousel >>> .v-window__container { align-items: center; }

  .gr-quote {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 24px;
    border: 0;
  }

  .gr-quote-text {
    font-size: 1.1rem;
    line-height: 1.58;
    color: var(--gr-ink);
    font-style: italic;
    margin-bottom: 16px;
  }

  .gr-quote-author {
    background: none;
    padding: 0;
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--gr-brand);
  }

  /* ---------- Passos ---------- */
  .gr-steps {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .gr-step {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 18px 0;
    border-bottom: 1px solid rgba(69, 78, 79, 0.16);
  }

  .gr-step:last-child { border-bottom: 0; }

  .gr-step-num {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--gr-brand);
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gr-step-title {
    display: block;
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 2px;
  }

  .gr-step-text {
    display: block;
    color: var(--gr-mute);
    line-height: 1.6;
  }

  /* ---------- Ubicació ---------- */
  #home .gr-location {
    min-height: auto;
    padding: 76px 0;
    background: var(--gr-alt);
    scroll-margin-top: 88px;
  }

  .gr-location-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    color: var(--gr-brand);
    font-weight: 700;
    text-decoration: none;
  }

  .gr-location-link .v-icon { color: var(--gr-brand); }

  .gr-location-link:hover { color: var(--gr-brand-dark); }

  .gr-map-card {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(69, 78, 79, 0.15);
    border-radius: 18px;
    box-shadow: 0 18px 42px rgba(37, 42, 43, 0.13);
  }

  .gr-map-card iframe {
    display: block;
    width: 100%;
    height: 360px;
    border: 0;
  }

  .gr-map-address {
    position: absolute;
    right: 16px;
    bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.94);
    border-radius: 8px;
    box-shadow: 0 5px 18px rgba(37, 42, 43, 0.15);
    color: var(--gr-slate);
    font-size: 0.86rem;
    font-weight: 600;
  }

  .gr-map-address .v-icon { color: var(--gr-brand); }

  /* ---------- Tancament ---------- */
  .gr-closing {
    position: relative;
    padding: 72px 0 88px;
    background-color: var(--gr-brand);
    background-image:
      linear-gradient(180deg, rgba(37, 42, 43, 0.55) 0%, rgba(110, 7, 36, 0.35) 100%),
      url("~@/assets/img/fondo.jpeg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed; /* efecto parallax: la imagen se queda fija al hacer scroll */
  }

  /* En móviles el fondo fijo no funciona bien (iOS lo ignora), pasa a scroll */
  @media (max-width: 900px) {
    .gr-closing { background-attachment: scroll; }
  }

  .gr-closing-text {
    font-size: 1.1rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.88);
    margin-bottom: 10px;
  }

  .gr-closing-sign {
    margin-top: 32px;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }

  /* ---------- FAB WhatsApp ---------- */
  .gr-fab {
    position: fixed;
    right: 20px;
    bottom: 76px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: #25D366;
    color: #fff !important;
    text-decoration: none;
    font-weight: 700;
    padding: 14px 22px;
    border-radius: 999px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    transition: transform 0.15s ease;
  }

  .gr-fab:hover { transform: translateY(-2px); }

  .gr-fab-icon.v-icon { color: #fff; font-size: 26px; }

  .gr-fab:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  /* ---------- Botó tornar a dalt ---------- */
  .gr-totop {
    position: fixed;
    right: 20px;
    bottom: 144px; /* just per sobre del FAB de WhatsApp */
    z-index: 20;
    width: 48px;
    height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #900A30;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    transition: transform 0.15s ease, background 0.15s ease;
  }

  .gr-totop:hover { transform: translateY(-2px); background: #6E0724; }

  .gr-totop-icon.v-icon { color: #fff; font-size: 28px; }

  .gr-totop:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 960px) {
    .gr-h1 { font-size: 2.1rem; }
    .gr-hero-title { font-size: 2.45rem; }
    .gr-h2 { font-size: 1.55rem; }
    .gr-big-number { font-size: 6rem; }
    .gr-anniversary-text { font-size: 1.1rem; text-align: center; }
    #home .gr-anniversary { min-height: 320px; }
    .gr-hero-support { justify-content: flex-start; }
    #home .gr-location { padding: 60px 0; }
  }

  @media (max-width: 600px) {
    #home .gr-hero { padding: 48px 0 40px; }
    .gr-section { padding: 48px 0; }
    .gr-h1 { font-size: 1.8rem; }
    .gr-hero-title { font-size: 2.15rem; }
    .gr-lead { font-size: 1.05rem; }
    .gr-hero-support { justify-content: center; }
    .gr-hero-card { padding: 28px 24px 24px; }
    .gr-map-card iframe { height: 300px; }
    .gr-map-address { right: 12px; bottom: 12px; }
    .gr-form-card { padding: 22px; }
    .gr-quote-text { font-size: 0.98rem; line-height: 1.5; }
    .gr-fab { padding: 0; width: 56px; height: 56px; justify-content: center; }
    .gr-fab-text { display: none; }

    /* Botons grans: que s'adaptin a l'amplada i parteixin el text al mòbil */
    .gr-cta.v-btn,
    .gr-hero-action.v-btn,
    .gr-wa-btn.v-btn,
    .gr-wa-btn--xl.v-btn {
      height: auto !important;
      min-height: 46px;
      max-width: 100%;
      white-space: normal;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
      font-size: 0.98rem;
    }
    .gr-cta.v-btn .v-btn__content,
    .gr-hero-action.v-btn .v-btn__content,
    .gr-wa-btn--xl.v-btn .v-btn__content {
      white-space: normal;
      max-width: 100%;
      line-height: 1.25;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .gr-fab, .gr-big-number, .gr-hero-action.v-btn { transition: none; }
    .gr-big-number, .gr-big-label, .gr-anniversary-text,
    .gr-hero-eyebrow, .gr-hero-title, .gr-hero-lead, .gr-hero-action { animation: none; }
  }
</style>
